/**
 * 에디터 공유 타입 + 상수 (JSX 없음)
 *
 * 아이콘맵은 각 컴포넌트(.tsx) 안에서 직접 정의.
 */

import { type ComponentType } from 'react'
import { SECTION_LABELS, type SectionId } from '@cugumi/shared/schemas'
import { GreetingSection } from './sections/GreetingSection.js'
import { ParentsSection } from './sections/ParentsSection.js'
import { CeremonySection } from './sections/CeremonySection.js'
import { GallerySection } from './sections/GallerySection.js'
import { ContactSection } from './sections/ContactSection.js'
import { AccountsSection } from './sections/AccountsSection.js'
import { RsvpSection } from './sections/RsvpSection.js'
import { GuestbookSection } from './sections/GuestbookSection.js'
import { TimelineSection } from './sections/TimelineSection.js'
import { ScheduleSection } from './sections/ScheduleSection.js'
import { NoticesSection } from './sections/NoticesSection.js'
import { FontStyleSection } from './sections/FontStyleSection.js'
import { BgmSection } from './sections/BgmSection.js'
import { KakaoShareSection } from './sections/KakaoShareSection.js'
import { SettingsSection } from './sections/SettingsSection.js'

/* ------------------------------------------------------------------ */
/* 타입                                                                */
/* ------------------------------------------------------------------ */

export type SettingSectionId = 'fontStyle' | 'bgm' | 'kakaoShare' | 'settings'
export type AllSectionId = SectionId | SettingSectionId

/* ------------------------------------------------------------------ */
/* 라벨                                                                */
/* ------------------------------------------------------------------ */

export const SETTING_LABELS: Record<SettingSectionId, string> = {
  fontStyle: '폰트 및 스타일',
  bgm: '배경음악',
  kakaoShare: '카카오톡 공유',
  settings: '설정',
}

export function getSectionLabel(id: AllSectionId): string {
  if (id in SECTION_LABELS) return SECTION_LABELS[id as SectionId]
  return SETTING_LABELS[id as SettingSectionId] ?? id
}

/* ------------------------------------------------------------------ */
/* 컴포넌트맵                                                          */
/* ------------------------------------------------------------------ */

export const SECTION_COMPONENTS: Record<SectionId, ComponentType> = {
  greeting: GreetingSection,
  parents: ParentsSection,
  ceremony: CeremonySection,
  gallery: GallerySection,
  contacts: ContactSection,
  accounts: AccountsSection,
  rsvp: RsvpSection,
  guestbook: GuestbookSection,
  timeline: TimelineSection,
  schedule: ScheduleSection,
  notices: NoticesSection,
}

export const SETTING_COMPONENTS: Record<SettingSectionId, ComponentType> = {
  fontStyle: FontStyleSection,
  bgm: BgmSection,
  kakaoShare: KakaoShareSection,
  settings: SettingsSection,
}

export function getSectionComponent(id: AllSectionId): ComponentType | null {
  return (SECTION_COMPONENTS as Record<string, ComponentType>)[id]
    ?? (SETTING_COMPONENTS as Record<string, ComponentType>)[id]
    ?? null
}

/* ------------------------------------------------------------------ */
/* 토글 가능 섹션                                                       */
/* ------------------------------------------------------------------ */

export const TOGGLEABLE_SECTIONS: Partial<Record<SectionId, string>> = {
  greeting: 'greeting',
  gallery: 'gallery',
  contacts: 'contacts',
  accounts: 'account',
  rsvp: 'rsvp',
  guestbook: 'guestbook',
  timeline: 'timeline',
  schedule: 'schedule',
  notices: 'notices',
}

/* ------------------------------------------------------------------ */
/* 설정 섹션 ID 목록                                                    */
/* ------------------------------------------------------------------ */

export const SETTING_IDS: SettingSectionId[] = ['fontStyle', 'bgm', 'kakaoShare', 'settings']

/* ------------------------------------------------------------------ */
/* 콘텐츠 섹션 그룹 (데스크탑 SectionNav용)                              */
/* ------------------------------------------------------------------ */

export const CONTENT_SECTION_GROUPS: { label: string; sections: SectionId[] }[] = [
  { label: '기본 정보', sections: ['parents', 'ceremony'] },
  { label: '콘텐츠', sections: ['greeting', 'gallery'] },
  { label: '추가 기능', sections: ['contacts', 'accounts', 'rsvp', 'guestbook', 'timeline', 'schedule', 'notices'] },
]
